import {
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	computed,
	effect,
	ElementRef,
	inject,
	OnDestroy,
	signal,
	viewChild,
	viewChildren,
} from '@angular/core';
import { TransportCoachListStore } from './_store/transport-coach-list.store';
import { TopBarComponent } from '../_shared/topbar/top-bar.component';
import { TransportAction, TransportModule, TransportSide } from '../_models/transport.model';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatTableModule } from '@angular/material/table';
import { BusComponent, SeatStatus } from '@oskar-frontend/comfort-package';
import {
	CoachSlotGroupItem,
	CouchGroupByVehicleEnum,
	CouchGroupByVehicles,
	CouchGroupedByVehicleAndLevel,
	Seats,
	VehicleLayout,
} from '../_models/transport-coach-list.model';
import { MatTabsModule } from '@angular/material/tabs';
import { CustomMatLoaderComponent } from '@/common/custom-mat-loader/custom-mat-loader.component';
import { MatSort, MatSortModule, Sort } from '@angular/material/sort';
import { sortCompare } from '@/_utils/sort-compare';
import { MatTooltipModule } from '@angular/material/tooltip';
import { RouterModule } from '@angular/router';
import { HoverHighlightService } from './services/hover-highlight.service';
import { MatDialog } from '@angular/material/dialog';
import {
	CoachListConfirmationComponent,
	CoachListConfirmationDialogData,
} from './coach-list-confirmation/coach-list-confirmation.component';
import { filter, take } from 'rxjs';
import { MatSnackBar, MatSnackBarRef, SimpleSnackBar } from '@angular/material/snack-bar';
import { MatButtonModule } from '@angular/material/button';
import { TransportCoachTableComponent } from './transport-coach-table/transport-coach-table.component';
import { NgClass } from '@angular/common';

const DEFAULT_DATA_TO_UPDATE = { activeCoach: null, seatId: null, selectedSlot: null };

@Component({
	selector: 'app-transport-coach-list',
	standalone: true,
	imports: [
		BusComponent,
		TopBarComponent,
		MatExpansionModule,
		MatTableModule,
		MatTabsModule,
		CustomMatLoaderComponent,
		MatSortModule,
		MatTooltipModule,
		RouterModule,
		MatButtonModule,
		TransportCoachTableComponent,
		NgClass,
	],
	providers: [TransportCoachListStore],
	templateUrl: './transport-coach-list.component.html',
	styleUrl: './transport-coach-list.component.scss',
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TransportCoachListComponent implements OnDestroy {
	// enums
	readonly TransportAction = TransportAction;
	readonly TransportModule = TransportModule;
	readonly TransportSide = TransportSide;

	readonly displayedColumns = computed(() => {
		const base = [
			'no',
			'slotId',
			'firstName',
			'lastName',
			'reservationId',
			'notesText',
			'slotsInReservation',
			'stop',
			'hasPack',
			'seat',
			'remove',
		];

		const hasBeforeAfter = this.store
			.couchGroupedByVehicle()
			.some((v) => v.slots?.some((s) => !!s.vehicleAfter && !!s.vehicleBefore));

		if (!hasBeforeAfter) {
			return base;
		}

		const seatIdx = base.indexOf('seat');
		return seatIdx === -1
			? base
			: [...base.slice(0, seatIdx), 'vehicleBefore', 'vehicleAfter', ...base.slice(seatIdx)];
	});

	readonly unassignedDisplayedColumns: string[] = [
		'no',
		'slotId',
		'firstName',
		'lastName',
		'reservationId',
		'notesText',
		'slotsInReservation',
		'stop',
		'hasPack',
	];

	readonly CouchGroupByVehicleEnum = CouchGroupByVehicleEnum;

	readonly busRefs = viewChildren('busRef', { read: ElementRef });
	readonly sectionRefs = viewChildren('sectionRef', { read: ElementRef });
	readonly sort = viewChild<MatSort>(MatSort);

	readonly highlightedSlotIds = signal(new Set<number>());

	readonly selectedDataToUpdate = signal<{
		activeCoach: CouchGroupByVehicles;
		seatId: number;
		selectedSlot: CoachSlotGroupItem;
	}>(DEFAULT_DATA_TO_UPDATE);

	readonly chosenSeats = computed(() => {
		const seatIdsByCouch = this.store
			.couchGroupedByVehicle()
			.map((couch) =>
				couch.slots
					.filter((slot) => slot.slotVehicle?.seat.kind === SeatStatus.SEAT)
					.map((slot) => slot.slotVehicle?.seat.id),
			);

		return seatIdsByCouch;
	});

	readonly couchGroupedByVehicleAndLevel = computed<CouchGroupedByVehicleAndLevel[]>(() => {
		const vehicles = this.store.couchGroupedByVehicle();
		const groupedVehicles = vehicles.map((vehicle) => this.mapVehicleToGroupedByLevel(vehicle));

		this.sortSlotsForSelectedVehicle(groupedVehicles);

		return groupedVehicles;
	});

	readonly dataIsLoading = computed(() => this.store.coachListIsLoading() || this.store.layoutIsLoading());

	readonly hasDoubledSlots = computed(() =>
		this.store.couchGroupedByVehicle().some((couch) => couch.slots.some((slot) => !!slot.doubledSlots)),
	);

	readonly gotUnassignedTab = computed(() =>
		this.couchGroupedByVehicleAndLevel().some((item) => item.type === CouchGroupByVehicleEnum.UNASSIGNED),
	);

	readonly someSlotNumberIsNull = computed(() =>
		this.store
			.coachList()
			.some((couch) => couch.slotVehicles.some((slotVehicle) => slotVehicle.seat.number === null)),
	);

	readonly expansionPanelToDisable = computed<CouchGroupByVehicleEnum>(() => {
		const selectedCoach = this.store
			.coachList()
			.find((slot) => slot.id === this.selectedDataToUpdate().selectedSlot?.id);

		if (!selectedCoach) {
			return;
		}

		if (this.selectedDataToUpdate().selectedSlot.slotVehicle === null) {
			const side = this.store.transport()?.side;
			const sideSlotVehicles = selectedCoach.slotVehicles.filter((v) => v.side === side);
			const hasBeforeOrder = sideSlotVehicles.some((v) => v.order === 0);
			const hasAfterOrder = sideSlotVehicles.some((v) => v.order === 1);

			if (hasBeforeOrder && !hasAfterOrder) {
				return CouchGroupByVehicleEnum.BEFORE;
			}

			return CouchGroupByVehicleEnum.AFTER;
		} else {
			if (this.selectedDataToUpdate().selectedSlot.slotVehicle.order === 0) {
				return CouchGroupByVehicleEnum.AFTER;
			} else if (this.selectedDataToUpdate().selectedSlot.slotVehicle.order === 1) {
				return CouchGroupByVehicleEnum.BEFORE;
			}
		}
	});

	readonly store = inject(TransportCoachListStore);

	private doubledSlotsSnackBar?: MatSnackBarRef<SimpleSnackBar>;

	private readonly rowElementsMap = signal<Map<number, ElementRef[]>>(new Map());

	private readonly rowElements = computed(() => [...this.rowElementsMap().values()].flat());

	private readonly sortingArray = signal<{ sortState: Sort; vehicleIndex: number }[]>([]);

	private readonly hoverHighlightService = inject(HoverHighlightService);
	private readonly cdr = inject(ChangeDetectorRef);
	private readonly dialog = inject(MatDialog);
	private readonly snackBar = inject(MatSnackBar);

	constructor() {
		effect(() => {
			this.onRefChange(0);
		});

		effect(
			() => {
				if (Object.values(this.selectedDataToUpdate()).some((value) => value === null)) {
					return;
				}

				const { selectedSlot, seatId, activeCoach } = this.selectedDataToUpdate();

				this.store.updateSlotSeat(selectedSlot, seatId, activeCoach);

				this.selectedDataToUpdate.set(DEFAULT_DATA_TO_UPDATE);
			},
			{ allowSignalWrites: true },
		);

		effect(() => {
			if (this.hasDoubledSlots()) {
				this.openDoubledSlotsSnackBar();
			} else {
				this.doubledSlotsSnackBar?.dismiss();
			}
		});
	}

	ngOnDestroy(): void {
		this.hoverHighlightService.cleanup();
	}

	onSort(sortState: Sort, vehicleIndex: number): void {
		const sortToSet: Sort = !!sortState.direction ? sortState : { active: 'id', direction: 'asc' };

		this.sortingArray.set([
			...this.sortingArray().filter((v) => v.vehicleIndex !== vehicleIndex),
			{ sortState: sortToSet, vehicleIndex },
		]);

		this.sectionRefs()[vehicleIndex]?.nativeElement.scrollTo({
			top: 0,
			behavior: 'smooth',
		});
	}

	onRefChange(index: number): void {
		const busRef = this.busRefs()[index]?.nativeElement;
		const sectionRef = this.sectionRefs()[index]?.nativeElement;
		if (busRef && sectionRef) {
			sectionRef.style.height = `${busRef.offsetHeight}px`;
			const rowEls = [...this.rowElements()];
			this.hoverHighlightService.bindHoverEvents(
				rowEls,
				(el) => JSON.parse(el.getAttribute('data-row') || '{}'),
				this.findSlotsByReservationIds.bind(this),
				(ids) => {
					this.highlightedSlotIds.set(ids);
				},
				this.cdr,
			);
		}
	}

	chooseSeat(seatId: number, activeCoach: CouchGroupByVehicles): void {
		const buildOriginalMap = () =>
			new Map(
				this.store
					.layouts()
					.flatMap((c) => c.seats)
					.map((seat) => [seat.id, seat.kind] as const),
			);

		const restoreKind = (id: number, map: Map<number, SeatStatus>) => {
			const original = map.get(id);
			return original === SeatStatus.EMERGENCY ? SeatStatus.EMERGENCY : SeatStatus.AVAILABLE;
		};

		const originalMap = buildOriginalMap();
		const seatToToggle = activeCoach.layout.seats.find((s) => s.id === seatId);

		if (!seatToToggle) {
			return;
		}

		const previouslyChosen = activeCoach.layout.seats.find((s) => s.kind === SeatStatus.CHOSEN && s.id !== seatId);
		if (previouslyChosen) previouslyChosen.kind = restoreKind(previouslyChosen.id, originalMap);

		const deselecting = seatToToggle.kind === SeatStatus.CHOSEN;
		seatToToggle.kind = deselecting ? restoreKind(seatToToggle.id, originalMap) : SeatStatus.CHOSEN;

		this.selectedDataToUpdate.set(
			deselecting
				? { ...this.selectedDataToUpdate(), seatId: null, activeCoach: null }
				: { ...this.selectedDataToUpdate(), seatId, activeCoach },
		);
	}

	chooseSlot(selectedSlot: CoachSlotGroupItem): void {
		const currentState = this.selectedDataToUpdate();
		const isSameSlot = currentState.selectedSlot?.id === selectedSlot.id;

		this.selectedDataToUpdate.set({
			...currentState,
			selectedSlot: isSameSlot ? null : selectedSlot,
		});
	}

	saveCoachList(): void {
		this.store.updateCoachList({ payload: this.store.coachList() });
	}

	onMouseLeaveSection(): void {
		/**
		 * Creates a new instance to ensure proper row selection after a view refresh.
		 *
		 * When the mouse leaves the component, the view is refreshed. As a result,
		 * Angular loses track of the previously selected row, causing the slot selection
		 * to disappear from the table.
		 *
		 * To resolve this, we reinitialize the selection logic immediately after the view
		 * refreshes, ensuring the correct row remains selected and visibly highlighted.
		 */
		const clonedDataToUpdate = { ...this.selectedDataToUpdate() };
		this.store.refreshLayouts();
		this.selectedDataToUpdate.set(clonedDataToUpdate);
	}

	resetCoachList(): void {
		this.dialog
			.open<CoachListConfirmationComponent, CoachListConfirmationDialogData>(CoachListConfirmationComponent, {
				maxWidth: '480px',
				data: {
					title: 'Resetowanie miejsc',
					message: `Na pewno chcesz przywrócić domyślny układ siedzeń?<br />
			Wszystkie ręczne zmiany zostaną usunięte, a układ cofnięty do stanu zaraz po automatycznym przydzieleniu
			miejsc.<br />
			<b>Kontynuować?</b>`,
				},
			})
			.afterClosed()
			.pipe(filter((confirmed) => confirmed))
			.subscribe(() => {
				this.store.changeLoadingState('coachList', true);
				this.store.fetchCoachList({
					transportId: this.store.transport()?.id,
					reassignAll: true,
					copyFromDeparture: false,
				});
			});
	}

	copyCoachList(): void {
		this.dialog
			.open<CoachListConfirmationComponent, CoachListConfirmationDialogData>(CoachListConfirmationComponent, {
				maxWidth: '480px',
				data: {
					title: 'kopiowanie z wyjazdu',
					message: `Na pewno chcesz skopiować układ siedzeń z wyjazdu?<br />
			Wszystkie ręczne zmiany z powrotu zostaną usunięte.<br />
			<b>Kontynuować?</b>`,
				},
			})
			.afterClosed()
			.pipe(filter((confirmed) => confirmed))
			.subscribe(() => {
				this.store.changeLoadingState('coachList', true);
				this.store.fetchCoachList({
					transportId: this.store.transport()?.id,
					reassignAll: true,
					copyFromDeparture: true,
				});
			});
	}

	removeSeat(coach: CoachSlotGroupItem): void {
		if (coach.hasPack) {
			this.dialog
				.open<CoachListConfirmationComponent, CoachListConfirmationDialogData>(CoachListConfirmationComponent, {
					maxWidth: '480px',
					data: {
						title: 'Usuwanie miejsc',
						message: `Ten uczestnik ma wybrany <b>PAKIET KOMFORT</b><br />
			Czy na pewno chcesz usunąć miejsce?`,
					},
				})
				.afterClosed()
				.pipe(filter(Boolean))
				.subscribe(() => {
					this.store.removeSlotSeat(coach);
				});
			return;
		}
		this.store.removeSlotSeat(coach);
	}

	onRowsReady(rows: ElementRef[], index: number): void {
		this.rowElementsMap.set(
			new Map<number, ElementRef[]>(
				[...this.rowElementsMap()].map(([key, value]) => (key === index ? [key, rows] : [key, value])),
			),
		);

		if (!this.rowElementsMap().has(index)) {
			this.rowElementsMap().set(index, rows);
		}
	}

	private mapVehicleToGroupedByLevel(vehicle: CouchGroupByVehicles): CouchGroupedByVehicleAndLevel {
		if (!vehicle.layout) {
			return {
				...vehicle,
				floors: null,
			};
		}
		const allSeats = vehicle.layout.seats;
		const isOneLevelBus = allSeats.every((seat) => seat.level === 0);

		if (isOneLevelBus) {
			return {
				...vehicle,
				floors: [{ level: 0, layout: { ...vehicle.layout, seats: allSeats } }],
			};
		}

		const groupedSeatsByLevel = this.groupSeatsByLevel(allSeats);

		const floors = Object.entries(groupedSeatsByLevel).map(([level, seats]) => ({
			level: Number(level),
			layout: { ...vehicle.layout, seats },
		}));

		return {
			...vehicle,
			floors,
		};
	}

	private groupSeatsByLevel(seats: Seats[]): Record<number, Seats[]> {
		return seats.reduce<Record<number, Seats[]>>((acc, seat) => {
			(acc[seat.level] ??= []).push(seat);
			return acc;
		}, {});
	}

	private sortSlotsForSelectedVehicle(groupedVehicles: CouchGroupedByVehicleAndLevel[]): void {
		this.sortingArray().forEach((sortingData) => {
			if (!sortingData.sortState) {
				return;
			}

			const { direction, active } = sortingData.sortState;
			const vehicleToSort = groupedVehicles[sortingData.vehicleIndex];

			if (!vehicleToSort) {
				return;
			}

			const isAsc = direction === 'asc';

			const stopSide = this.store.transport().side === TransportSide.RETURN ? 'Return' : 'Departure';

			vehicleToSort.slots = [...vehicleToSort.slots].sort((a, b) => {
				switch (active) {
					case 'reservationId':
						return sortCompare(a.reservation, b.reservation, isAsc);
					case 'slotId':
						return sortCompare(a.id, b.id, isAsc);
					case 'hasPack':
						return sortCompare(Number(a.hasPack), Number(b.hasPack), isAsc);
					case 'firstName':
						return sortCompare(a.userData?.firstName, b.userData?.firstName, isAsc);
					case 'lastName':
						return sortCompare(a.userData?.lastName, b.userData?.lastName, isAsc);
					case 'seat':
						return sortCompare(Number(a.slotVehicle.seat.number), Number(b.slotVehicle.seat.number), isAsc);
					case 'vehicleBefore':
						return sortCompare(a.vehicleBefore?.name, b.vehicleBefore?.name, isAsc);
					case 'vehicleAfter':
						return sortCompare(a.vehicleAfter?.name, b.vehicleAfter?.name, isAsc);
					case 'slotsInReservation':
						return sortCompare(Number(a.slotsInReservation), Number(b.slotsInReservation), isAsc);
					case 'stop':
						return sortCompare(a[`stop${stopSide}`]?.stop, b[`stop${stopSide}`]?.stop, isAsc);
					default:
						return sortCompare(a.id, b.id, isAsc);
				}
			});
		});
	}

	private findSlotsByReservationIds(vehicleId: number, reservation: number, order: number): number[] {
		const vehicleGroup = this.couchGroupedByVehicleAndLevel().find(
			(v) => v.vehicle?.id === vehicleId && v.order === order,
		);

		if (!vehicleGroup) {
			return [];
		}

		if (vehicleGroup.type === CouchGroupByVehicleEnum.UNASSIGNED) {
			return vehicleGroup.slots.filter((slot) => slot.reservation === reservation).map((slot) => slot.id);
		}

		const matchingSlotIds = vehicleGroup.slots
			.filter((slot) => slot.reservation === reservation && slot.slotVehicle.seat.number !== null)
			.map((slot) => slot.slotVehicle.seat.id);

		if (matchingSlotIds.length === 0) {
			return [];
		}

		const updateSeatKinds = (layout: VehicleLayout): VehicleLayout => ({
			...layout,
			seats: layout.seats.map((seat) => {
				if (matchingSlotIds.includes(seat.id)) {
					return { ...seat, kind: SeatStatus.COACH_HOVERED };
				}
				if (seat.kind === SeatStatus.COACH_HOVERED) {
					return { ...seat, kind: SeatStatus.SEAT };
				}
				return seat;
			}),
		});

		vehicleGroup.layout = updateSeatKinds(vehicleGroup.layout);
		vehicleGroup.floors = vehicleGroup.floors.map((floor) => ({
			...floor,
			layout: updateSeatKinds(floor.layout),
		}));

		return vehicleGroup.slots.filter((slot) => slot.reservation === reservation).map((slot) => slot.id);
	}

	private openDoubledSlotsSnackBar(): void {
		if (this.doubledSlotsSnackBar) {
			return;
		}

		this.doubledSlotsSnackBar = this.snackBar.open(
			'Na którymś z pojazdów dwóch uczestników jest przypisanych do tego samego miejsca. Zweryfikuj!',
			'OK',
		);

		this.doubledSlotsSnackBar
			.afterDismissed()
			.pipe(take(1))
			.subscribe(() => (this.doubledSlotsSnackBar = undefined));
	}
}
